import type { ListBuilderConfiguration, ListBuilderList } from '../types';

/**
 * PD-16532 — the trip builder's account list (Distribution → Trips → Create
 * trip, step 2): every active customer, ranked by how much it needs ice, with
 * what is already waiting for it and whether it is on a trip that day.
 *
 * Like Replenishment, the rows come from a service (`DispatchCandidatesService`
 * behind GET /api/dispatch/candidates) rather than a table:
 * `objectTable: 'dispatch_candidate'` only scopes the field and filter keys.
 * The service reads the filter keys and builds the SQL, including the need
 * score the default sort uses, so ranking and paging happen in the database.
 */
const NEED_LEVEL_VALUE_MAPPING = [
	{ value: 'now', label: 'Needs ice now', color: { type: 'danger' } },
	{ value: 'soon', label: 'Needs ice soon', color: { type: 'warning' } },
	{ value: 'ok', label: 'OK', color: { type: 'success' } },
	{ value: 'unknown', label: 'No signal', color: { type: 'neutral' } },
];

const BLOCKED_VALUE_MAPPING = [
	{ value: 'credit_hold', label: 'Credit hold', color: { type: 'danger' } },
];

const field = (fieldName: string) => ({
	fieldName,
	objectTable: 'dispatch_candidate',
});

const defaultDispatchCandidateList: ListBuilderList = {
	id: 'default-dispatch-candidates',
	name: 'Accounts',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {},
	columns: [
		{
			id: 'dispatch-candidate-account',
			fields: [
				{
					id: 'dispatch-candidate-name',
					dataConfig: { type: 'field', field: field('name'), defaultValue: '-' },
					link: { enabled: true, type: 'object', objectTable: 'client', idField: 'clientId' },
					styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				{
					id: 'dispatch-candidate-account-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'dispatch-candidate-address',
					dataConfig: { type: 'field', field: field('addressLabel'), defaultValue: 'No address' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
				// PD-16956 (F-41) — "On this trip" (or the other trip it is on)
				// under the name, so it shows in the narrow Map view too, where
				// the Trip column is hidden and an account already added looked
				// like any other.
				{
					id: 'dispatch-candidate-account-trip-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'tripLabel', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-candidate-account-trip',
					dataConfig: { type: 'field', field: field('tripLabel') },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'statusInfo' } },
					displayStyle: 'text',
					conditionalVisibility: { field: 'tripLabel', condition: 'is_not_null' },
				},
			],
			label: 'Account',
			headers: [{ id: 'h-dispatch-candidate-account', text: 'Account' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('name') }],
			minWidth: 240,
			widthConfig: 'large',
		},
		{
			id: 'dispatch-candidate-need',
			fields: [
				{
					id: 'dispatch-candidate-need-level',
					dataConfig: { type: 'field', field: field('needLevel') },
					styleConfig: { size: 'sm', valueMapping: NEED_LEVEL_VALUE_MAPPING },
					displayStyle: 'badge',
				},
				{
					id: 'dispatch-candidate-need-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
				},
				{
					id: 'dispatch-candidate-need-label',
					dataConfig: { type: 'field', field: field('needLabel'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Need',
			headers: [{ id: 'h-dispatch-candidate-need', text: 'Need' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'DESC', ...field('need') }],
			// Fixed, not a preset: a medium column is squeezed to its header
			// width when the list is narrow (beside the map), which cut the
			// badge to "Needs ice no…". The badge is the column's point.
			width: 150,
		},
		{
			id: 'dispatch-candidate-waiting',
			fields: [
				{
					id: 'dispatch-candidate-waiting-label',
					dataConfig: { type: 'field', field: field('waitingLabel'), defaultValue: '-' },
					// PD-16114 — the three Waiting lines wrap: beside the map at
					// 1280px a single line cut "Later: 3 orders · Drop-o…" with
					// nothing to read the rest from.
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
						allowTextWrap: true,
					},
					displayStyle: 'text',
				},
				// PD-16114 — "Drop-off 2 merchandisers · 10/9" on its own line,
				// only when there is equipment work waiting.
				{
					id: 'dispatch-candidate-waiting-equipment-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'equipmentWaitingLabel', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-candidate-waiting-equipment',
					dataConfig: { type: 'field', field: field('equipmentWaitingLabel') },
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
						allowTextWrap: true,
					},
					displayStyle: 'text',
					conditionalVisibility: { field: 'equipmentWaitingLabel', condition: 'is_not_null' },
				},
				// PD-16114 — the lines above are what this trip would carry (due
				// by its date); orders dated later are summed apart here so a
				// pick-up booked for the 26th does not read as work for the 22nd.
				{
					id: 'dispatch-candidate-waiting-later-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'waitingLaterLabel', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-candidate-waiting-later',
					dataConfig: { type: 'field', field: field('waitingLaterLabel') },
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'textSecondary' },
						allowTextWrap: true,
					},
					displayStyle: 'text',
					conditionalVisibility: { field: 'waitingLaterLabel', condition: 'is_not_null' },
				},
				// PD-16958 — "Skipped on 10/10 trip · Store Closed": why an order
				// that already went out on a truck is waiting again.
				{
					id: 'dispatch-candidate-waiting-missed-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'waitingMissedLabel', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-candidate-waiting-missed',
					dataConfig: { type: 'field', field: field('waitingMissedLabel') },
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'theme', themeColor: 'warning' },
						allowTextWrap: true,
					},
					displayStyle: 'text',
					conditionalVisibility: { field: 'waitingMissedLabel', condition: 'is_not_null' },
				},
			],
			label: 'Waiting',
			headers: [{ id: 'h-dispatch-candidate-waiting', text: 'Waiting' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			// Fixed for the same reason as Need: "1 order · $7.45" was cut short.
			// PD-16114 — wide enough for "5 orders · $1,288.80 · deliver 10/22"
			// and the drop-off / later lines, which were cut at 190.
			width: 270,
		},
		{
			id: 'dispatch-candidate-last-delivered',
			fields: [
				{
					id: 'dispatch-candidate-last-delivered-date',
					dataConfig: { type: 'field', field: field('lastDeliveryDate'), defaultValue: 'Never' },
					styleConfig: {
						size: 'md',
						colorRule: { type: 'default' },
						formatOptions: { relativeBase: 'today', relativeUnit: 'day' },
					},
					displayStyle: 'relative_date',
				},
			],
			label: 'Last delivered',
			headers: [{ id: 'h-dispatch-candidate-last-delivered', text: 'Last delivered' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: true,
			sortFields: [{ direction: 'ASC', ...field('lastDelivered') }],
			minWidth: 120,
			widthConfig: 'small',
		},
		{
			id: 'dispatch-candidate-trip',
			fields: [
				{
					id: 'dispatch-candidate-trip-label',
					dataConfig: { type: 'field', field: field('tripLabel'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				// An empty badge renders "-", so the credit-hold line shows only
				// when there is a hold; otherwise the column read "- -".
				{
					id: 'dispatch-candidate-trip-break',
					dataConfig: { type: 'custom_text', customText: '' },
					displayStyle: 'line_break',
					conditionalVisibility: { field: 'blockedReason', condition: 'is_not_null' },
				},
				{
					id: 'dispatch-candidate-blocked',
					dataConfig: { type: 'field', field: field('blockedReason') },
					styleConfig: { size: 'sm', valueMapping: BLOCKED_VALUE_MAPPING },
					displayStyle: 'badge',
					conditionalVisibility: { field: 'blockedReason', condition: 'is_not_null' },
				},
			],
			label: 'Trip',
			headers: [{ id: 'h-dispatch-candidate-trip', text: 'Trip' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 140,
			widthConfig: 'medium',
		},
		{
			id: 'dispatch-candidate-route',
			fields: [
				{
					id: 'dispatch-candidate-route-names',
					dataConfig: { type: 'field', field: field('routeNames'), defaultValue: '-' },
					styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
					displayStyle: 'text',
				},
			],
			label: 'Route',
			headers: [{ id: 'h-dispatch-candidate-route', text: 'Route' }],
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			sortable: false,
			minWidth: 120,
			widthConfig: 'small',
		},
	],
	defaultSortId: 'dispatch-candidate-need-desc',
	actions: [],
};

export const DISPATCH_CANDIDATE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [defaultDispatchCandidateList],
	tableSorts: [
		{
			id: 'dispatch-candidate-need-desc',
			name: 'Need (High to Low)',
			description: 'Accounts closest to running out first',
			fields: [{ direction: 'DESC', ...field('need') }],
		},
		{
			id: 'dispatch-candidate-last-delivered-asc',
			name: 'Longest since delivery',
			fields: [{ direction: 'ASC', ...field('lastDelivered') }],
		},
		{
			id: 'dispatch-candidate-city-asc',
			name: 'City (A-Z)',
			fields: [{ direction: 'ASC', ...field('city') }],
		},
		{
			id: 'dispatch-candidate-name-asc',
			name: 'Name (A-Z)',
			fields: [{ direction: 'ASC', ...field('name') }],
		},
	],
	showResetButton: true,
	filters: {
		activeFilters: [
			'dispatchSearch',
			'dispatchNeedsIce',
			'dispatchHasOrder',
			// PD-16114
			'dispatchDueByTripDate',
			'dispatchEquipmentWaiting',
			'dispatchDueWithinDays',
			'dispatchNotDeliveredDays',
			'dispatchBelowThreshold',
			'dispatchSensorDown',
			'dispatchRoute',
		],
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Account',
			objectTable: 'dispatch_candidate',
			selectedFields: [
				{ fieldName: 'name', fieldLabel: 'Account' },
				{ fieldName: 'addressLabel', fieldLabel: 'Address' },
				{ fieldName: 'needLabel', fieldLabel: 'Need' },
				{ fieldName: 'waitingLabel', fieldLabel: 'Waiting' },
				{ fieldName: 'equipmentWaitingLabel', fieldLabel: 'Drop-offs / pick-ups' },
				{ fieldName: 'waitingLaterLabel', fieldLabel: 'Waiting later' },
				{ fieldName: 'waitingMissedLabel', fieldLabel: 'Last stop missed' },
				{ fieldName: 'lastDeliveryDate', fieldLabel: 'Last delivered' },
				{ fieldName: 'tripLabel', fieldLabel: 'Trip' },
				{ fieldName: 'routeNames', fieldLabel: 'Route' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
